import { type, Schema, ArraySchema } from "@colyseus/schema";
import { GameMode, GameModeStatus } from './GameMode';
import { GameModeAdvancedSettings, CustomBaseMode, DamageScaling } from "./GameModeAdvancedSettings";


export class GameModeSettingsSchema extends Schema {
    // Base properties that all game modes have
    @type("string") gameMode: GameMode = GameMode.TIMED;
    @type("int16") maxRounds: number = 5;
    @type("int16") timerSeconds: number = 60; // Use -1 for null/no timer
    @type("boolean") hasSpecialRules: boolean = false;
    @type("string") status: GameModeStatus = GameModeStatus.AVAILABLE;
    @type("int8") minPlayers: number = 1;
    @type("int8") maxPlayers: number = 8;

    // Fight mode specific properties
    @type("int32") teamMaxHp: number = 10000;
    @type("int8") numberOfTeams: number = 2;
    @type("string") damageMultiplierScaling: DamageScaling = 'normal';

    // Daily mode specific properties
    @type("string") dailySeed: string = "";

    // Game seed for recreating games
    @type("string") gameSeed: string = "";

    // Flash mode specific properties
    @type("int16") flashDuration: number = 1000;

    // Hazard mode specific properties
    @type("int8") hazardsPerRound: number = 1;
    @type(["string"]) enabledHazards = new ArraySchema<string>(); // Enabled hazards for Hazard mode (empty = all enabled)

    // Custom mode specific properties
    @type("string") baseMode: string = "timed"; // Base mode for custom mode
    @type("boolean") hasFlashMode: boolean = false; // Whether flash mode is enabled in custom mode
    @type("boolean") hasHazards: boolean = false; // Whether hazards are enabled in custom mode
    @type("boolean") compassDisabled: boolean = false; // Whether compass is disabled in custom mode

    // Helper method to check if timer is enabled (timerSeconds > 0)
    get hasTimer(): boolean {
        return this.timerSeconds > 0;
    }

    // Helper method to get actual timer value or null
    get actualTimerSeconds(): number | null {
        return this.timerSeconds > 0 ? this.timerSeconds : null;
    }

    // Method to populate from GameModeAdvancedSettings interface
    fromAdvancedSettings(settings: GameModeAdvancedSettings): void {
        this.gameMode = settings.gameMode;
        this.maxRounds = settings.maxRounds === -1 ? -1 : settings.maxRounds;
        this.timerSeconds = settings.timerSeconds ?? -1; // Use -1 for null
        this.hasSpecialRules = settings.hasSpecialRules;
        this.status = settings.status;
        this.minPlayers = settings.minPlayers;
        this.maxPlayers = settings.maxPlayers;

        // Mode-specific properties
        if ('teamMaxHp' in settings && settings.teamMaxHp !== undefined) {
            this.teamMaxHp = settings.teamMaxHp;
        }
        if ('numberOfTeams' in settings && settings.numberOfTeams !== undefined) {
            this.numberOfTeams = settings.numberOfTeams;
        }
        if ('damageMultiplierScaling' in settings && settings.damageMultiplierScaling !== undefined) {
            this.damageMultiplierScaling = settings.damageMultiplierScaling;
        }

        // Coop mode uses numberOfTeams (already handled above)

        if ('dailySeed' in settings && settings.dailySeed !== undefined) {
            this.dailySeed = settings.dailySeed;
        }

        if ('flashDuration' in settings && settings.flashDuration !== undefined) {
            this.flashDuration = settings.flashDuration;
        }

        if ('hazardsPerRound' in settings && settings.hazardsPerRound !== undefined) {
            this.hazardsPerRound = settings.hazardsPerRound;
        }

        if ('enabledHazards' in settings && settings.enabledHazards) {
            this.enabledHazards.clear();
            settings.enabledHazards.forEach(hazard => this.enabledHazards.push(hazard));
        }

        // Custom mode specific properties
        if ('baseMode' in settings && settings.baseMode !== undefined) {
            this.baseMode = settings.baseMode;
        }
        if ('hasFlashMode' in settings && settings.hasFlashMode !== undefined) {
            this.hasFlashMode = settings.hasFlashMode;
        }
        if ('hasHazards' in settings && settings.hasHazards !== undefined) {
            this.hasHazards = settings.hasHazards;
        }
        if ('compassDisabled' in settings && settings.compassDisabled !== undefined) {
            this.compassDisabled = settings.compassDisabled;
        }
    }

    // Method to convert back to GameModeAdvancedSettings interface
    toAdvancedSettings(): GameModeAdvancedSettings {
        switch (this.gameMode) {
            case GameMode.FIGHT:
                return {
                    gameMode: GameMode.FIGHT,
                    maxRounds: -1,
                    timerSeconds: this.timerSeconds > 0 ? this.timerSeconds : 60,
                    hasSpecialRules: this.hasSpecialRules,
                    status: this.status,
                    minPlayers: this.minPlayers,
                    maxPlayers: this.maxPlayers,
                    teamMaxHp: this.teamMaxHp,
                    numberOfTeams: this.numberOfTeams,
                    damageMultiplierScaling: this.damageMultiplierScaling
                };
            case GameMode.DAILY:
                return {
                    gameMode: GameMode.DAILY,
                    maxRounds: this.maxRounds,
                    timerSeconds: this.timerSeconds > 0 ? this.timerSeconds : 0,
                    hasSpecialRules: this.hasSpecialRules,
                    status: this.status,
                    minPlayers: this.minPlayers,
                    maxPlayers: this.maxPlayers,
                    dailySeed: this.dailySeed
                };
            case GameMode.FLASH:
                return {
                    gameMode: GameMode.FLASH,
                    maxRounds: this.maxRounds,
                    timerSeconds: null,
                    hasSpecialRules: this.hasSpecialRules,
                    status: this.status,
                    minPlayers: this.minPlayers,
                    maxPlayers: this.maxPlayers,
                    flashDuration: this.flashDuration
                };
            case GameMode.HAZARD:
                return {
                    gameMode: GameMode.HAZARD,
                    maxRounds: this.maxRounds,
                    timerSeconds: this.timerSeconds > 0 ? this.timerSeconds : 60,
                    hasSpecialRules: this.hasSpecialRules,
                    status: this.status,
                    minPlayers: this.minPlayers,
                    maxPlayers: this.maxPlayers,
                    hazardsPerRound: this.hazardsPerRound,
                    enabledHazards: this.enabledHazards.length > 0 ? Array.from(this.enabledHazards) : undefined
                };
            case GameMode.CLASSIC:
                return {
                    gameMode: GameMode.CLASSIC,
                    maxRounds: this.maxRounds,
                    timerSeconds: null,
                    hasSpecialRules: this.hasSpecialRules,
                    status: this.status,
                    minPlayers: this.minPlayers,
                    maxPlayers: this.maxPlayers
                };
            case GameMode.TIMED:
                return {
                    gameMode: GameMode.TIMED,
                    maxRounds: this.maxRounds,
                    timerSeconds: this.timerSeconds > 0 ? this.timerSeconds : 0,
                    hasSpecialRules: this.hasSpecialRules,
                    status: this.status,
                    minPlayers: this.minPlayers,
                    maxPlayers: this.maxPlayers
                };
            case GameMode.ENDLESS:
                return {
                    gameMode: GameMode.ENDLESS,
                    maxRounds: -1,
                    timerSeconds: null,
                    hasSpecialRules: this.hasSpecialRules,
                    status: this.status,
                    minPlayers: this.minPlayers,
                    maxPlayers: this.maxPlayers
                };
            case GameMode.COOP:
                return {
                    gameMode: GameMode.COOP,
                    maxRounds: this.maxRounds,
                    timerSeconds: this.timerSeconds > 0 ? this.timerSeconds : 60,
                    hasSpecialRules: this.hasSpecialRules,
                    status: this.status,
                    minPlayers: this.minPlayers,
                    maxPlayers: this.maxPlayers,
                    numberOfTeams: this.numberOfTeams
                };
            case GameMode.CUSTOM:
                return {
                    gameMode: GameMode.CUSTOM,
                    baseMode: this.baseMode as CustomBaseMode,
                    maxRounds: this.maxRounds,
                    timerSeconds: this.timerSeconds > 0 ? this.timerSeconds : null,
                    hasSpecialRules: this.hasSpecialRules,
                    status: this.status,
                    minPlayers: this.minPlayers,
                    maxPlayers: this.maxPlayers,
                    hasFlashMode: this.hasFlashMode,
                    flashDuration: this.flashDuration,
                    hasHazards: this.hasHazards,
                    hazardsPerRound: this.hazardsPerRound,
                    enabledHazards: this.enabledHazards.length > 0 ? Array.from(this.enabledHazards) : undefined,
                    // Fight/Coop mode properties
                    teamMaxHp: this.teamMaxHp,
                    numberOfTeams: this.numberOfTeams,
                    damageMultiplierScaling: this.damageMultiplierScaling,
                    // Compass property
                    compassDisabled: this.compassDisabled
                };
            default:
                throw new Error("Unknown game mode");
        }
    }
}